import { useCallback, useMemo, useState } from "react";

interface UsePaginationOptions<T> {
	items: T[];
	itemsPerPage?: number;
}

interface UsePaginationReturn<T> {
	page: number;
	totalPages: number;
	currentItems: T[];
	handlePageChange: (_event: React.ChangeEvent<unknown>, value: number) => void;
	setPage: (page: number) => void;
}

const DEFAULT_ITEMS_PER_PAGE = 10;

export const usePagination = <T>({
	items,
	itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
}: UsePaginationOptions<T>): UsePaginationReturn<T> => {
	const [page, setPage] = useState(1);

	const totalPages = Math.ceil(items.length / itemsPerPage);

	const currentItems = useMemo(() => {
		const startIndex = (page - 1) * itemsPerPage;
		return items.slice(startIndex, startIndex + itemsPerPage);
	}, [items, page, itemsPerPage]);

	const handlePageChange = useCallback((_event: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	const pagination = useMemo(
		() => ({
			page,
			totalPages,
			currentItems,
			handlePageChange,
			setPage,
		}),
		[page, totalPages, currentItems, handlePageChange],
	);

	return pagination;
};
